import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { createUseStyles } from 'react-jss'
import { responsiveDinamicStyles } from '../../utils/styles'
import mediaQueries from '../../constants/mediaQueries'

import './rf-contentItem.scss'

function generateDynamicStyles(props) {
	const styleProps = {
		gap: props.gap
	}

	const rows = props.rows
	const cols = props.cols
	const rowHeight = props.rowHeight

	let styles = {}
	Object.entries(styleProps).forEach(([propKey, propValue]) => {
		// if prop is not "undefined" or "null"
		if (propValue !== null && typeof propValue !== 'undefined') {
			// if props is unique value,
			if (typeof propValue === 'number') {
				if (propKey === 'gap') {
					let height = 'auto'
					if (rowHeight !== 'auto' && rowHeight !== 'square') {
						height = rowHeight * rows + propValue * (rows - 1)
					}
					styles = {
						...styles,
						height: height === 'auto' ? height : height + 'px'
					}
				}
			}
			// if prop is an object {sm: 5, md....}, generate css rule for each one
			else if (typeof propValue === 'object') {
				Object.keys(propValue).forEach((mq) => {
					if (mediaQueries[mq]) {
						let cssValue = {}
						if (propKey === 'gap') {
							let height = 'auto'
							if (rowHeight !== 'auto' && rowHeight !== 'square') {
								height = rowHeight * rows + propValue[mq] * (rows - 1)
							}
							cssValue = { height: height === 'auto' ? height : height + 'px' }
						}
						const cssResult = responsiveDinamicStyles(mq, { ...cssValue })
						// medaiQueries are objects within the styles object
						// we need to track their key to avoid overwrite them
						if ('mediaQkey' in cssResult) {
							styles[cssResult['mediaQkey']] = {
								...styles[cssResult['mediaQkey']],
								...cssResult['stylesVal']
							}
						} else {
							styles = {
								...styles,
								...cssResult
							}
						}
					}
				})
			}
		}
	})
	return styles
}

const squareStyles = {
	gridItem: {
		position: 'relative',
		'& img': {
			position: 'absolute',
			width: '100%',
			height: '100%',
			objectFit: 'cover',
			objectPosition: 'center'
		}
	}
}

const styles = {
	// Dynamic CSS
	styles: (props) => ({
		...generateDynamicStyles(props)
	}),
	...squareStyles
}
const useStyles = createUseStyles(styles, { name: 'Vb' })

const ContentItem = forwardRef((props, ref) => {
	const {
		children,
		className,
		cols = 1,
		component: Component = 'li',
		rows = 1,
		style,
		rowHeight = 'auto',
		gap,
		...other
	} = props

	const classes = useStyles(props)

	return (
		<Component
			ref={ref}
			className={clsx('rf-contentitem', className, classes.styles, rowHeight === 'square' ? classes.gridItem : null)}
			style={
				rowHeight !== 'square'
					? {
						gridColumnEnd: `span ${cols}`,
						gridRowEnd: `span ${rows}`,
						...style
					}
					: null
			}
			{...other}
		>
			{React.Children.map(children, (child) => {
				if (!React.isValidElement(child)) {
					return null
				}
				if (child.type === 'img') {
					return React.cloneElement(child, child.props.className)
				}
				return child
			})}
		</Component>
	)
})

ContentItem.displayName = 'ContentItem'

export default ContentItem

ContentItem.defaultProps = {
	rows: 1,
	cols: 1
}

ContentItem.propTypes = {
	/**
	 * While you can pass any node as children,
	 * the main use case is for an img.
	 */
	children: PropTypes.node,
	/**
	 * Override or extend the styles applied to the component.
	 */
	classes: PropTypes.object,
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * Width of the item in number of grid columns.
	 * @default 1
	 */
	cols: PropTypes.number,
	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * By default, is an <li> element.
	 */
	component: PropTypes.elementType,
	/**
	 * Height of the item in number of grid rows.
	 * @default 1
	 */
	rows: PropTypes.number,
	/**
	 * @ignore
	 */
	style: PropTypes.object,
	/**
	 * @ignore
	 */
	value: PropTypes.object
}
